import { Check, CirclePlusIcon, Loader2 } from 'lucide-react'
import React, { useState } from 'react'
import { Drawer, DrawerContent, DrawerTrigger } from 'src/components/ui/drawer'
import { Bundle } from '../page'
import { Label } from 'src/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from 'src/components/ui/popover'
import { Combobox } from 'src/components/ui/combobox'
import { Checkbox } from 'src/components/ui/checkbox'
import { useAppSelector } from 'src/hooks/redux'
import { RootState } from 'src/store'
import useAuth from 'src/hooks/useAuth'
import { Button } from 'src/components/ui/button'
import { getWindevLang } from 'src/lib/lang'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useMutation } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import axios from 'src/axios/axios'
import { PDFDocument } from 'pdf-lib';
import { Base64 } from 'js-base64';

type AddBundleToFitdocProps = {
    bundle: Bundle
}

const AddBundleToFitdoc = ({bundle}:AddBundleToFitdocProps) => {
    const { auth } = useAuth()
    const { i18n } = useTranslation()
    const dossiers:DossierObject[] = useAppSelector((state:RootState) => state.dossiers);
    const doctypes:DoctypeObject[] = useAppSelector((state:RootState) => state.doctypes);

    const [filter_dossiers_mine, setfilter_dossiers_mine] = useState(true);

    const [selected_dossier, setselected_dossier] = useState<string | undefined>();
    const [selected_doctype, setselected_doctype] = useState<string | undefined>();

    const { mutate, isPending, isSuccess } = useMutation({
        mutationFn: async () => {
            const b64OfPDF = await generatePDFBase64(bundle.documents)

            let body:any = {
                token: auth.data?.token,
                file: b64OfPDF,
                filename: `${removeNonAlphanumeric(bundle.name)}.pdf`,
                doctype: selected_doctype,
            }


            const { data } = await axios.post('documents/add', body)
            console.log(data);

            if(data.errorcode!==0){ throw new Error(data.message) }
            toast.success('Document succesvol toegevoegd', { id:'add-fd' })
            
        },
        onError: (e) => {
            console.log(e);
            toast.error('Document toevoegen mislukt', { id:'add-fd' })
        }
    })

    // Function to generate a PDF from an array of base64 strings
    async function generatePDFBase64(base64Pages:string[]) {
        const pdfDoc = await PDFDocument.create();
        const pagesCount = base64Pages.length;

        for (let i = 0; i < pagesCount; i++) {
            const page = pdfDoc.addPage();
            let base64Data = base64Pages[i];

            // Remove data URI prefix if present
            if (base64Data.startsWith('data:image/png;base64,')) {
                base64Data = base64Data.slice('data:image/png;base64,'.length);
            }

            try {
                // Decode base64 to Uint8Array
                const arrayBuffer = base64ToArrayBuffer(base64Data);
                const imageBytes = new Uint8Array(arrayBuffer);

                // Embed image into PDF
                const image = await pdfDoc.embedPng(imageBytes);
                const { width, height } = image.scale(0.5);

                page.drawImage(image, {
                    x: page.getWidth() / 2 - width / 2,
                    y: page.getHeight() / 2 - height / 2,
                    width,
                    height,
                    opacity: 0.75,
                });
            } catch (error) {
                console.error(`Error processing page ${i + 1}:`, error);
                throw error; // Propagate error for handling
            }
        }

        // Save the PDF document to bytes
        const pdfBytes = await pdfDoc.save();

        // Convert PDF bytes to base64
        const pdfBase64 = await bytesToBase64(pdfBytes);

        return pdfBase64;
    }

    // Function to convert base64 to ArrayBuffer
    function base64ToArrayBuffer(base64:string) {
        const binaryString = atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; ++i) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    }

    // Function to convert bytes to base64
    async function bytesToBase64(bytes:any) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result?.toString().replace(/^data:(.*,)?/, ''));
            reader.onerror = reject;
            reader.readAsDataURL(new Blob([bytes], { type: 'application/pdf' }));
        });
    }

    function removeNonAlphanumeric(str:string) {
        return str.replace(/[^a-zA-Z0-9 ]/g, '');
    }

    function countDossierFilters () {
        let count = 0;
        if(filter_dossiers_mine){ count++ }
        return count
    }

    function createDossierOptions () {
        let d = Array.isArray(dossiers) ? dossiers : []
        if(filter_dossiers_mine){ d = d.filter(i=>i.owner === auth.data?.user.fitdocusername) }
        
        let cbList = d.map(d=>({value:d.dossierentityid, label:removeNonAlphanumeric(d.extendeddescription)})).filter(i => i.label.length > 0)
        return cbList;
    }

    function isComplete () {
        try {
            const s = z.object({
                dossier: z.string().min(1).optional(),
                doctype: z.string().min(1)
            })
            s.parse({
                dossier: selected_dossier,
                doctype: selected_doctype
            })
            return true
        } catch (err) {
            return false
        }
    }

    let dossiersOptions = createDossierOptions()
    let doctypeOptions = Array.isArray(doctypes) ? doctypes.filter(i=>i.rights.find(i=>i.create!==0)).map(i=>({value:i.doctype, label:i.docoms.find(i=>i.nationid===getWindevLang(i18n.language))?.oms || i.doctype})) : []

  return (
    <Drawer>
        <DrawerTrigger asChild>
            <button className='flex flex-col items-center justify-center gap-2 text-xs text-neutral-500 whitespace-nowrap'>
                <CirclePlusIcon className='w-5 h-5 min-h-5' strokeWidth={1.5}/>
                Toevoegen aan FITdoc
            </button>
        </DrawerTrigger>
        <DrawerContent handleClassName='bg-black/50' className='px-4 pb-4'>
            <div className='flex flex-col gap-6'>
                {/* <div>
                    <div className='flex items-end justify-between mb-1'>
                        <Label>Dossier</Label>
                        <Popover>
                            <PopoverTrigger asChild><button className='text-sm text-primary font-medium bg-primary/10 rounded-sm px-2'>Filters ({countDossierFilters()})</button></PopoverTrigger>
                            <PopoverContent>
                                <div className='grid grid-cols-[20px,1fr] gap-4'>
                                    <div className='flex items-center'><Checkbox checked={filter_dossiers_mine} onCheckedChange={(c)=>{setfilter_dossiers_mine(c as boolean)}} id='my-files'/></div>
                                    <div className='flex items-center'><Label htmlFor='my-files'>Mijn dossiers</Label></div>
                                </div>
                            </PopoverContent>
                        </Popover>
                    </div>
                    <Combobox selectedValue={selected_dossier?.toString()} onValueChange={(v)=>{setselected_dossier(v.value.toString())}} options={dossiersOptions} placeholder='Zoeken...'/>
                </div> */}
                <div>
                    <Label>Documenttype<span className='text-primary ml-1'>*</span></Label>
                    <Combobox selectedValue={selected_doctype?.toString()} onValueChange={(v)=>{setselected_doctype(v.value.toString())}} options={doctypeOptions} placeholder='Zoeken...'/>
                </div>
                <Button onClick={()=>{mutate()}} disabled={!isComplete() || isSuccess || isPending} className='gap-2'>
                    {isPending && <Loader2 className='w-4 h-4 animate-spin'/>}
                    {isSuccess && <Check className='w-4 h-4'/>}
                    Document toevoegen
                </Button>
            </div>
        </DrawerContent>
    </Drawer>
  )
}

export default AddBundleToFitdoc